import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { SkillAuditEvent, SkillAuditRun } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface SkillAuditPanelProps {
  skillId: string;
}

const STATUS_LABEL: Record<SkillAuditRun['status'], string> = {
  pending: '큐 대기',
  running: '점검 중',
  completed: '점검 완료',
  failed: '실패',
  cancelled: '취소됨',
};

/**
 * Skill detail 페이지의 "AI 데이터 점검" 섹션.
 *
 * - 점검은 graphile-worker 백그라운드 task. 화면은 폴링으로 latest run 의 진행/결과를 비추기만 한다.
 * - 새로고침 / 다른 화면 이동 후 복귀해도 latest run 으로 자동 reattach.
 * - 결과가 완료되고 변경이 있으면 우측 textarea 편집 + "적용" 으로 systemPrompt 갱신.
 */
export function SkillAuditPanel({ skillId }: SkillAuditPanelProps) {
  const qc = useQueryClient();
  const [editedPrompt, setEditedPrompt] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const latestQuery = useQuery({
    queryKey: ['admin', 'skills', skillId, 'audit-latest'],
    queryFn: () => api.adminSkillAuditRunsLatest(skillId),
    refetchInterval: (q) => {
      const run = q.state.data?.run;
      if (!run) return false;
      // 종료 상태 명시 (completedAt 보조 가드) — pending/running 외 모든 status 에서 멈춤.
      // 워커가 비정상 종료해 status='running' 잔존해도 completedAt 이 설정돼 있으면 stop.
      const terminal =
        run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled';
      if (terminal || run.completedAt) return false;
      return 1500;
    },
    refetchIntervalInBackground: false,
  });

  const run = latestQuery.data?.run ?? null;
  // completedAt 이 채워졌으면 status 와 무관하게 진행 중 아님.
  const isInflight =
    run !== null && !run.completedAt && (run.status === 'pending' || run.status === 'running');
  const isCompleted = run?.status === 'completed';
  const unchanged = isCompleted && run?.proposedSystemPrompt === run?.currentSystemPrompt;

  // 새 run 이 완료되어 들어오면 textarea 를 그 시점의 proposed 로 초기화.
  // 사용자가 편집 중이면 (editedPrompt !== null) 덮지 않음.
  useEffect(() => {
    if (isCompleted && run?.proposedSystemPrompt && editedPrompt === null) {
      setEditedPrompt(run.proposedSystemPrompt);
    }
    if (!isCompleted && editedPrompt !== null && run?.status !== 'completed') {
      // 새 점검 시작 등으로 completed 가 사라졌으면 textarea 초기화.
      setEditedPrompt(null);
    }
  }, [isCompleted, run?.proposedSystemPrompt, run?.status, editedPrompt]);

  const startMut = useMutation({
    mutationFn: () => api.adminSkillAuditStart(skillId),
    onSuccess: () => {
      setActionError(null);
      setFeedback(null);
      setEditedPrompt(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'skills', skillId, 'audit-latest'] });
    },
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : '점검 시작에 실패했습니다.'),
  });

  const cancelMut = useMutation({
    mutationFn: () => {
      if (!run) throw new Error('취소할 run 이 없습니다.');
      return api.adminSkillAuditRunCancel(skillId, run.id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'skills', skillId, 'audit-latest'] });
    },
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : '취소에 실패했습니다.'),
  });

  const commitMut = useMutation({
    mutationFn: () => {
      if (!run || !run.proposedSystemPrompt) throw new Error('적용할 결과가 없습니다.');
      return api.adminSkillAuditCommit(skillId, {
        proposedSystemPrompt: editedPrompt ?? run.proposedSystemPrompt,
        summary: run.summary ?? '데이터 점검',
        changedAreas: run.changedAreas,
        // LLM 이 cross-domain 동료 보완 제안을 보냈으면 commit 에 함께 박아 한 번에 적용.
        // null/undefined 면 기존 값 유지 (서버 라우트가 undefined 분기 처리).
        ...(run.proposedPairsWellWith !== null && run.proposedPairsWellWith !== undefined
          ? { pairsWellWith: run.proposedPairsWellWith }
          : {}),
      });
    },
    onSuccess: () => {
      setFeedback('새 system prompt 가 저장되었습니다.');
      setActionError(null);
      setEditedPrompt(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'skills', skillId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'skills', skillId, 'prompt-versions'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'skills', skillId, 'audit-latest'] });
    },
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : '저장에 실패했습니다.'),
  });

  return (
    <section className="mt-8 rounded-2xl border border-border bg-surface p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">AI 데이터 점검</h3>
          <p className="mt-0.5 text-xs text-foreground-muted">
            DB 도구를 사용하는 스킬의 systemPrompt 가 사용 데이터소스/테이블을 명확히 명시하는지 LLM
            이 점검합니다. 점검은 백그라운드에서 진행되어 새로고침/페이지 이동 후 돌아와도 진행
            상황을 그대로 볼 수 있습니다.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isInflight ? (
            <Button
              variant="ghost"
              onClick={() => cancelMut.mutate()}
              disabled={cancelMut.isPending}
            >
              <X className="size-4" />
              취소
            </Button>
          ) : null}
          <Button onClick={() => startMut.mutate()} disabled={startMut.isPending || isInflight}>
            {isInflight ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                점검 중…
              </>
            ) : (
              <>
                <Sparkles className="size-4" />
                {run ? '다시 점검' : 'AI 로 점검'}
              </>
            )}
          </Button>
        </div>
      </div>

      {feedback ? (
        <div className="mb-3 rounded-md border border-success/30 bg-success/5 px-3 py-2 text-xs text-success">
          {feedback}
        </div>
      ) : null}
      {actionError ? (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive whitespace-pre-wrap">
          {actionError}
        </div>
      ) : null}

      {!run ? (
        <p className="text-xs text-foreground-muted">
          아직 점검 이력이 없습니다. "AI 로 점검" 을 누르면 백그라운드에서 시작합니다.
        </p>
      ) : (
        <RunPanel
          run={run}
          isInflight={isInflight}
          isCompleted={!!isCompleted}
          unchanged={!!unchanged}
          editedPrompt={editedPrompt}
          onEditedChange={setEditedPrompt}
          onCommit={() => commitMut.mutate()}
          committing={commitMut.isPending}
        />
      )}
    </section>
  );
}

function RunPanel({
  run,
  isInflight,
  isCompleted,
  unchanged,
  editedPrompt,
  onEditedChange,
  onCommit,
  committing,
}: {
  run: SkillAuditRun;
  isInflight: boolean;
  isCompleted: boolean;
  unchanged: boolean;
  editedPrompt: string | null;
  onEditedChange: (v: string) => void;
  onCommit: () => void;
  committing: boolean;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'rounded-sm px-1.5 py-0.5 text-[10px] uppercase tracking-wider',
              run.status === 'running' && 'bg-[hsl(210_90%_55%)]/15 text-[hsl(210_90%_55%)]',
              run.status === 'pending' && 'bg-surface-muted text-foreground-muted',
              run.status === 'completed' && 'bg-success/15 text-success',
              run.status === 'failed' && 'bg-destructive/15 text-destructive',
              run.status === 'cancelled' && 'bg-surface-muted text-foreground-muted',
            )}
          >
            {STATUS_LABEL[run.status]}
          </span>
          <span className="text-foreground-muted">
            반복 {run.iterationsUsed}/10 · 시작 {new Date(run.createdAt).toLocaleString()}
          </span>
        </div>
        {isInflight ? (
          <span className="inline-flex items-center gap-1.5 text-foreground-muted">
            <Loader2 className="size-3 animate-spin" />
            백그라운드 진행 중
          </span>
        ) : null}
      </div>

      {run.error ? (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive whitespace-pre-wrap">
          {run.error}
        </div>
      ) : null}

      {run.events.length > 0 ? (
        <div className="mb-4 rounded-md border border-border bg-surface-muted/30 p-3">
          <div className="mb-2 text-[10px] uppercase tracking-wider text-foreground-muted">
            진행 상황
          </div>
          <ul className="max-h-64 space-y-1.5 overflow-y-auto">
            {run.events.map((e, idx) => (
              <EventRow key={`${e.kind}-${idx}-${e.at}`} ev={e} />
            ))}
          </ul>
        </div>
      ) : null}

      {isCompleted ? (
        <div className="rounded-md border border-border p-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h4 className="text-xs uppercase tracking-wider text-foreground-muted">
                점검 결과 — {unchanged ? '변경 없음' : 'diff 검토'}
              </h4>
              {run.summary ? <p className="mt-1 text-xs">{run.summary}</p> : null}
              {run.changedAreas.length > 0 ? (
                <p className="mt-0.5 text-[11px] text-foreground-muted">
                  변경 영역: {run.changedAreas.join(', ')}
                </p>
              ) : null}
              {run.proposedPairsWellWith !== null ? (
                <p className="mt-0.5 text-[11px] text-foreground-muted">
                  cross-domain 동료 제안:{' '}
                  {run.proposedPairsWellWith.length > 0
                    ? run.proposedPairsWellWith.join(', ')
                    : '(모두 제거)'}
                </p>
              ) : null}
            </div>
            <Button
              onClick={onCommit}
              disabled={
                committing ||
                unchanged ||
                (editedPrompt ?? run.proposedSystemPrompt ?? '').trim().length === 0
              }
            >
              {committing ? '저장 중…' : '적용'}
            </Button>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wider text-foreground-muted">
                현재
              </div>
              <pre className="max-h-96 min-h-64 overflow-y-auto rounded-md border border-border bg-background p-2 text-xs font-mono whitespace-pre-wrap">
                {run.currentSystemPrompt || '(비어있음)'}
              </pre>
            </div>
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wider text-foreground-muted">
                제안
              </div>
              <textarea
                value={editedPrompt ?? run.proposedSystemPrompt ?? ''}
                onChange={(e) => onEditedChange(e.target.value)}
                rows={20}
                className="min-h-64 w-full rounded-md border border-border bg-background p-2 text-xs font-mono"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EventRow({ ev }: { ev: SkillAuditEvent }) {
  if (ev.kind === 'progress') {
    return (
      <li className="rounded-md bg-surface-muted/50 px-3 py-1.5 text-xs">
        <span>{ev.message}</span>
      </li>
    );
  }
  if (ev.kind === 'iteration') {
    return (
      <li className="rounded-md bg-surface-muted/30 px-3 py-1.5 text-[10px] uppercase tracking-wider text-foreground-muted">
        반복 {ev.iter} / {ev.limit}
      </li>
    );
  }
  if (ev.kind === 'tool_start') {
    return (
      <li className="rounded-md bg-[hsl(210_90%_55%)]/10 text-[hsl(210_90%_55%)] px-3 py-1.5 text-xs flex items-start gap-2">
        <Loader2 className="size-3 mt-0.5 shrink-0 animate-spin" />
        <div className="min-w-0 flex-1">
          <span className="font-mono text-[10px] mr-2 opacity-80">{ev.toolName}</span>
          <span className="break-words">{ev.argsPreview || '(인자 없음)'}</span>
        </div>
      </li>
    );
  }
  // tool_done
  return (
    <li
      className={cn(
        'rounded-md px-3 py-1.5 text-xs flex items-start gap-2',
        ev.status === 'ok' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive',
      )}
    >
      <div className="min-w-0 flex-1">
        <span className="font-mono text-[10px] mr-2 opacity-80">{ev.toolName}</span>
        <span className="break-words">{ev.summary}</span>
      </div>
    </li>
  );
}
